/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { CommonModule, DatePipe } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject, debounceTime, takeUntil } from "rxjs";

// PrimeNG imports
import { MessageService } from "primeng/api";
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionPanel,
} from "primeng/accordion";
import {
  AutoCompleteCompleteEvent,
  AutoCompleteModule,
} from "primeng/autocomplete";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";
import { SkeletonModule } from "primeng/skeleton";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";

// Services
import {
  QPilotAccessToken,
  QPilotService,
  QPilotUser,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import { ProductGroupsService } from "../../services/product-groups.service";

// Models
import {
  QPilotProductGroup,
  QPilotProductGroupProduct,
} from "../../../../core/models/qpilot/product-group.interface";
import {
  IProductGroupFormControls,
  IProductSearchResult,
} from "../../interfaces/product-group.interface";

type PageMode = "create" | "edit";

/**
 * Product Group Edit/Create Component
 * Handles creating and editing product groups with product autocomplete
 */
@Component({
  selector: "app-product-group-edit",
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    FormsModule,
    ReactiveFormsModule,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    AutoCompleteModule,
    ButtonModule,
    CardModule,
    InputTextModule,
    SkeletonModule,
    ToastModule,
    TooltipModule,
  ],
  templateUrl: "./product-group-edit.component.html",
  styleUrl: "./product-group-edit.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Note: MessageService is NOT provided locally - we use the root instance
  // This ensures both component toast calls AND API interceptor error toasts
  // go to the same MessageService that the <p-toast> listens to
})
export class ProductGroupEditComponent implements OnInit, OnDestroy {
  // Dual mode inputs for web component/iframe support
  @Input() inputSiteId?: number;
  @Input() inputToken?: QPilotAccessToken;
  @Input() inputProductGroupId?: number;
  @Input() embeddedMode: boolean = false;

  private readonly elementRef = inject(ElementRef);
  private readonly destroy$ = new Subject<void>();

  // Page state
  mode: PageMode = "create";
  productGroupId: number | null = null;
  siteId: number | null = null;

  // Loading states
  readonly loading = signal(true);
  readonly saving = signal(false);

  // Product autocomplete state
  readonly productSuggestions = signal<IProductSearchResult[]>([]);
  selectedProduct: IProductSearchResult | null = null;

  // Products in the group
  readonly products = signal<QPilotProductGroupProduct[]>([]);

  // Original product group data (for edit mode)
  originalProductGroup: QPilotProductGroup | null = null;

  // Form
  productGroupForm!: FormGroup<IProductGroupFormControls>;

  // Page title and button label
  get pageTitle(): string {
    if (this.mode === "create") {
      return "New Group";
    }
    return this.originalProductGroup?.Name || "Group";
  }

  get submitButtonLabel(): string {
    if (this.saving()) {
      return this.mode === "create" ? "Creating..." : "Saving...";
    }
    return this.mode === "create" ? "Create" : "Update";
  }

  // Product summary counts
  get totalProductsCount(): number {
    return this.products().length;
  }

  get activeProductsCount(): number {
    return this.products().filter((p) => p.Product?.Active === true).length;
  }

  get inactiveProductsCount(): number {
    return this.products().filter((p) => p.Product?.Active === false).length;
  }

  get invalidProductsCount(): number {
    return this.products().filter((p) => p.Product?.Valid === false).length;
  }

  constructor(
    private readonly productGroupsService: ProductGroupsService,
    private readonly qpilotService: QPilotService,
    private readonly sessionService: SessionService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly messageService: MessageService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // Detect embedded mode from attribute (for web components)
    const element = this.elementRef.nativeElement as HTMLElement;
    const embeddedModeAttr =
      element.getAttribute("embedded-mode") ??
      element.getAttribute("embeddedMode");
    if (embeddedModeAttr === "true" || embeddedModeAttr === "") {
      this.embeddedMode = true;
    }

    // Get token from attribute in embedded mode
    if (this.embeddedMode) {
      const tokenString =
        element.getAttribute("input-token") ??
        element.getAttribute("inputToken");

      if (
        tokenString &&
        (!this.inputToken ||
          typeof this.inputToken !== "object" ||
          !this.inputToken.TokenBearerAuth)
      ) {
        this.inputToken = {
          TokenBearerAuth: tokenString,
          TokenAuth: "",
          Id: 0,
          UserId: 0,
          CreatedUtc: new Date(),
          UpdatedUtc: new Date(),
          ExpiresUtc: new Date(),
          User: {} as QPilotUser,
          FirstLogin: 0,
        };
      }

      // Get productGroupId from attribute if in edit mode
      const productGroupIdStr =
        this.inputProductGroupId?.toString() ??
        element.getAttribute("input-product-group-id") ??
        element.getAttribute("inputProductGroupId");
      if (productGroupIdStr) {
        this.inputProductGroupId = parseInt(productGroupIdStr, 10);
      }
    }

    this.determineMode();
    this.loadSiteId();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize the form
   */
  private initForm(): void {
    this.productGroupForm = new FormGroup<IProductGroupFormControls>({
      name: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)],
      }),
    });
  }

  /**
   * Get access token based on mode (embedded vs standard)
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    if (this.embeddedMode && this.inputToken) {
      return this.inputToken;
    }
    return this.sessionService.accessToken ?? undefined;
  }

  /**
   * Determine create vs edit mode from route or input
   */
  private determineMode(): void {
    if (this.embeddedMode) {
      // In embedded mode, check inputProductGroupId
      if (this.inputProductGroupId) {
        this.mode = "edit";
        this.productGroupId = this.inputProductGroupId;
      } else {
        this.mode = "create";
      }
    } else {
      // Standard mode: determine from URL
      const url = this.router.url;
      if (url.includes("/create")) {
        this.mode = "create";
      } else {
        this.mode = "edit";
        const idParam = this.route.snapshot.paramMap.get("id");
        if (idParam) {
          this.productGroupId = parseInt(idParam, 10);
        }
      }
    }
  }

  /**
   * Load site ID from route params/input and then load data
   */
  private loadSiteId(): void {
    let siteId: number | null = null;

    if (this.embeddedMode) {
      // Embedded mode: Get siteId from input or attribute
      const element = this.elementRef.nativeElement as HTMLElement;
      const siteIdString =
        this.inputSiteId?.toString() ??
        element.getAttribute("input-site-id") ??
        element.getAttribute("inputSiteId");

      if (siteIdString) {
        siteId = parseInt(siteIdString, 10);
      }
    } else {
      // Standard mode: Get site ID from parent route parameter
      siteId = Number(this.route.parent?.snapshot.paramMap.get("siteId"));
    }

    if (siteId && siteId > 0) {
      this.siteId = siteId;
      this.loadData();
    } else {
      this.loading.set(false);
      this.cdr.markForCheck();
    }
  }

  /**
   * Load product group data for edit mode
   */
  private loadData(): void {
    if (this.mode === "edit" && this.productGroupId && this.siteId) {
      this.loading.set(true);
      const accessToken = this.getAccessToken();

      if (!accessToken) {
        this.loading.set(false);
        return;
      }

      this.productGroupsService
        .getProductGroup(this.siteId, this.productGroupId, accessToken)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (productGroup) => {
            this.originalProductGroup = productGroup;
            this.populateForm(productGroup);
            this.loading.set(false);
            this.cdr.markForCheck();
          },
          error: () => {
            this.messageService.add({
              severity: "error",
              summary: "Error",
              detail: "Failed to load product group",
            });
            this.loading.set(false);
            this.cdr.markForCheck();
          },
        });
    } else {
      this.loading.set(false);
    }
  }

  /**
   * Populate form with product group data
   */
  private populateForm(productGroup: QPilotProductGroup): void {
    this.productGroupForm.patchValue({
      name: productGroup.Name || "",
    });

    // Load products
    if (productGroup.Products && productGroup.Products.length > 0) {
      this.products.set([...productGroup.Products]);
    }
  }

  /**
   * Navigate back to list
   */
  goBack(): void {
    if (!this.siteId) return;

    if (this.embeddedMode) {
      // Emit event for parent to handle navigation
      const event = new CustomEvent("qmc-navigate-back", {
        detail: {
          component: "product-group-edit",
          targetComponent: "product-groups-list",
          siteId: this.siteId,
          productGroupId: this.productGroupId,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      void this.router.navigate([
        "/widgets/dashboard",
        this.siteId,
        "product-groups",
      ]);
    }
  }

  /**
   * Product autocomplete search with debounce
   * Matches old QMC: 300ms debounce, filters out variable products
   */
  productSearch(event: AutoCompleteCompleteEvent): void {
    if (!this.siteId) return;

    const query = event.query || "";
    const accessToken = this.getAccessToken();

    if (!accessToken) return;

    this.productGroupsService
      .searchProducts(this.siteId, query, accessToken)
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          // Filter out products already in the group
          const currentIds = new Set(
            this.products().map((p) => p.Id || p.Product?.ExternalId),
          );
          const filtered = results.filter((r) => !currentIds.has(r.ExternalId));
          this.productSuggestions.set(filtered);
          this.cdr.markForCheck();
        },
        error: () => {
          this.productSuggestions.set([]);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Handle product selection from autocomplete
   * Adds product to the group
   */
  productSelected(event: { value: IProductSearchResult }): void {
    const product = event.value;
    if (!product) return;

    // Add product to the group
    const newProduct: QPilotProductGroupProduct = {
      Id: product.ExternalId,
      Title: product.Title,
    };

    this.products.update((current) => [...current, newProduct]);

    // Clear selection
    this.selectedProduct = null;
    this.cdr.markForCheck();
  }

  /**
   * Remove a product from the group
   */
  removeProduct(product: QPilotProductGroupProduct): void {
    this.products.update((current) =>
      current.filter((p) => p.Id !== product.Id),
    );
    this.cdr.markForCheck();
  }

  /**
   * Submit form
   */
  onSubmit(): void {
    this.productGroupForm.markAllAsTouched();

    if (this.productGroupForm.invalid) {
      this.messageService.add({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please fill in all required fields",
      });
      return;
    }

    if (!this.siteId) {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "No site selected",
      });
      return;
    }

    const formData = this.productGroupForm.getRawValue();
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "Not authenticated",
      });
      return;
    }

    this.saving.set(true);
    this.cdr.markForCheck();

    if (this.mode === "create") {
      this.createProductGroup(formData, accessToken);
    } else {
      this.updateProductGroup(formData, accessToken);
    }
  }

  /**
   * Create new product group
   */
  private createProductGroup(
    formData: { name: string },
    accessToken: QPilotAccessToken,
  ): void {
    const request = {
      Name: formData.name,
      SiteId: this.siteId!,
      Products: this.products().map((p) => ({
        Id: p.Id,
        Title: p.Title,
      })),
    };

    this.productGroupsService
      .createProductGroup(this.siteId!, request, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.navigateAfterSave();
        },
        error: () => {
          this.saving.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Update existing product group
   */
  private updateProductGroup(
    formData: { name: string },
    accessToken: QPilotAccessToken,
  ): void {
    if (!this.productGroupId || !this.originalProductGroup) return;

    const productGroup: QPilotProductGroup = {
      ...this.originalProductGroup,
      Name: formData.name,
      Products: this.products().map((p) => ({
        Id: p.Id,
        Title: p.Title,
      })),
    };

    this.productGroupsService
      .updateProductGroup(this.siteId!, productGroup, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.navigateAfterSave();
        },
        error: () => {
          this.saving.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Navigate after save operation
   */
  private navigateAfterSave(): void {
    if (this.embeddedMode) {
      // Emit saved event for parent to handle
      const event = new CustomEvent("qmc-product-group-saved", {
        detail: {
          component: "product-group-edit",
          action: "list",
          productGroupId: this.productGroupId,
          siteId: this.siteId,
          mode: this.mode,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      // Standard mode: navigate after delay
      setTimeout(() => this.goBack(), 1000);
    }
  }

  /**
   * Check if a field has an error
   */
  hasError(path: string): boolean {
    const control = this.productGroupForm.get(path);
    return !!(control?.invalid && control?.touched);
  }

  /**
   * Get error message for a field
   */
  getErrorMessage(path: string): string {
    const control = this.productGroupForm.get(path);
    if (!control?.errors) return "";

    if (control.errors["required"]) return "This field is required";
    if (control.errors["maxlength"]) {
      const maxLengthError = control.errors["maxlength"] as {
        requiredLength: number;
      };
      return `Maximum ${maxLengthError.requiredLength} characters allowed`;
    }

    return "Invalid value";
  }

  /**
   * Get product display name for autocomplete item template
   */
  getProductDisplayName(product: IProductSearchResult): string {
    return product?.Title || "";
  }

  /**
   * Get product external ID for display in autocomplete suggestions
   */
  getProductExternalId(product: IProductSearchResult): string {
    return product?.ExternalId || "";
  }
}
